import { MESSAGE_POLICY, textSafety } from '@mogzo/text-safety';
import { z } from 'zod';
import type { ChatCorner, ChatLook } from './multiplayer';

/**
 * Chat on MOGZO (M38): direct messages and groups, with one inbox for the site and, from M38b,
 * games. Accounts 18+ only until the legal review for minors (M46). Every message is checked
 * by text safety's message policy; staff read a chat only when a message in it is reported.
 */

export const CHAT_LIMITS = {
  /** Characters in one message. */
  messageLength: 1000,
  /** Messages a player sends a minute, across all their chats. */
  messagesPerMinute: 30,
  /** New chats (direct or group) a player starts a day. */
  newChatsPerDay: 20,
  /** Requests a player has sent that are still waiting to be accepted. */
  requestsWaiting: 10,
  /** People in a group, its maker included. */
  groupSize: 10,
  groupName: 40,
  /** An account this young can't send links yet. */
  linkAccountDays: 3,
  /** A declined request: the same person can ask again after this. */
  declinedDays: 30,
  /** Messages on one page of a chat. */
  page: 40,
  /** Messages kept with a report, before the reported one. */
  reportContext: 20,
  reportNote: 500,
} as const;

export const CHAT_REQUEST_SETTINGS = ['everyone', 'played', 'nobody'] as const;
export type ChatRequestSetting = (typeof CHAT_REQUEST_SETTINGS)[number];

export const CHAT_REPORT_REASONS = [
  'bullying',
  'hate',
  'threats',
  'sexual',
  'spam',
  'self_harm',
  'other',
] as const;
export type ChatReportReason = (typeof CHAT_REPORT_REASONS)[number];

/** What players pick when reporting, and what staff see. */
export const CHAT_REPORT_REASON_NAMES: Record<ChatReportReason, string> = {
  bullying: 'Bullying or harassment',
  hate: 'Hate or slurs',
  threats: 'Threats of violence',
  sexual: 'Sexual messages',
  spam: 'Spam or a scam',
  self_harm: 'They may hurt themselves',
  other: 'Something else',
};

/** How long staff turn a player's chat off: 7 or 30 days, or until staff turn it back on. */
export const CHAT_OFF_LENGTHS = ['7', '30', 'staff'] as const;
export type ChatOffLength = (typeof CHAT_OFF_LENGTHS)[number];

/** Links: anything that looks like a web address. New accounts can't send them yet. */
export const CHAT_LINK =
  /(?:https?:\/\/|www\.)\S|\b[a-z0-9-]+\.(?:com|net|org|io|gg|tv|app|xyz|link|ru|cn)\b/i;

/**
 * Text safety's message policy on a chat message: `block` (slurs, threats) is refused, `mask`
 * has swearing hidden in `masked`, and `review` (talk of one's own self-harm) is sent, with a
 * flag for staff. The API decides; the app can run it too for instant feedback.
 */
export function checkChatText(text: string): {
  action: 'allow' | 'mask' | 'review' | 'block';
  masked: string | null;
} {
  const verdict = textSafety.check(text, MESSAGE_POLICY);
  const hidden = verdict.findings
    .filter((finding) => finding.action === 'mask')
    .sort((a, b) => a.start - b.start);
  if (!hidden.length) return { action: verdict.action, masked: null };
  let masked = '';
  let at = 0;
  for (const { start, end } of hidden) {
    if (start < at) continue;
    masked += text.slice(at, start) + '*'.repeat(end - start);
    at = end;
  }
  return { action: verdict.action, masked: masked + text.slice(at) };
}

export const chatTextSchema = z
  .string()
  .trim()
  .min(1, 'Write a message first.')
  .max(CHAT_LIMITS.messageLength, `Keep it under ${CHAT_LIMITS.messageLength} characters.`);

export const chatSettingsSchema = z.object({
  requests: z.enum(CHAT_REQUEST_SETTINGS),
  hideSwearing: z.boolean(),
  readMarks: z.boolean(),
});

export interface ChatPerson {
  id: string | null;
  /** "Deleted player" once their account is gone. */
  username: string;
}

export interface ChatMessageView {
  id: string;
  conversationId: string;
  from: ChatPerson;
  mine: boolean;
  /** The text as this player sees it: swearing hidden if they asked. Empty once deleted. */
  text: string;
  deleted: boolean;
  /** A quick phrase (M38b), which games can show over the player. */
  phrase: boolean;
  createdAt: string;
}

export interface ChatConversationSummary {
  id: string;
  /** `match`: the players of one match (M38b), shown while it's on and for a day after. */
  kind: 'direct' | 'group' | 'match';
  /** The other person for a direct chat; the group's name; the game's name for a match. */
  name: string;
  /** Everyone else in it, for avatars. */
  people: ChatPerson[];
  /** `request`: someone new wrote, and this player hasn't accepted yet. */
  status: 'accepted' | 'request';
  last: { text: string; fromMe: boolean; from: string; at: string } | null;
  unread: number;
  muted: boolean;
}

export interface ChatInbox {
  conversations: ChatConversationSummary[];
  /** Chats with something unread (the messages icon's number), and requests waiting. */
  unread: number;
  requests: number;
}

export interface ChatMember extends ChatPerson {
  owner: boolean;
  addedAt: string;
}

export interface ChatConversationView extends ChatConversationSummary {
  members: ChatMember[];
  /** Whether this player made the group (adds and removes people). */
  owner: boolean;
  /** For a direct chat: when they joined MOGZO and the games you've played together. */
  about: { joinedAt: string; playedTogether: string[] } | null;
  messages: ChatMessageView[];
  hasMore: boolean;
  /** The newest of this player's messages the others have read, if read marks are on for both. */
  seenUpTo: string | null;
  /** Why this player can't send here, if they can't. */
  cantSend: string | null;
  createdAt: string;
}

/** A match's chat, as the play page and the game's chat open it (M38b). */
export interface MatchChatView {
  conversationId: string;
  /** What the mode lets players send. */
  chat: 'phrases' | 'typed';
  look: ChatLook;
  corner: ChatCorner;
  /** MOGZO's phrases, then the game's. */
  phrases: string[];
  /** The newest messages, oldest first. */
  messages: ChatMessageView[];
  /** Everyone in the match who can chat, this player included. */
  players: ChatPerson[];
  /** Why this player can't send, if they can't (their chat is off). */
  cantSend: string | null;
  /** Whether the match is still on: once it ends, the chat is read-only. */
  open: boolean;
}

export interface ChatSettings {
  requests: ChatRequestSetting;
  hideSwearing: boolean;
  readMarks: boolean;
  blocked: { username: string; at: string }[];
  /** Set while staff have turned this player's chat off; `until` null means until staff turn it back on. */
  off: { until: string | null; reason: string } | null;
  /** When this account can send links. */
  linksFrom: string | null;
}

/** What the live connection tells a tab. */
export type ChatLiveEvent =
  | { type: 'message'; message: ChatMessageView }
  | { type: 'deleted'; conversationId: string; messageId: string }
  | { type: 'read'; conversationId: string }
  | { type: 'changed'; conversationId: string | null }
  | { type: 'bell' };
